import { AppConfig } from '@ltvco/refresh-lib/ctx';
import {
  Avatar,
  Box,
  DarkTooltip,
  Stack,
  Text,
  useTheme,
} from '@ltvco/refresh-lib/theme';
import { useScreenSize } from '@ltvco/refresh-lib/utils';
import { useAccount, useSession } from '@ltvco/refresh-lib/v1';
import {
  Add as AddIcon,
  InsertDriveFile as InsertDriveFileIcon,
  KeyboardTab,
  KeyboardBackspace,
  Person as PersonIcon,
  QuestionAnswer,
} from '@mui/icons-material';
import { useContext, useState, useRef } from 'react';

import {
  NewChatButton,
  SidebarIcon,
  HelpModal,
  ReportUsageCard,
  ProfileModal,
  BrandLogoChat,
} from 'components/ChatBV/components';
import {
  CHATBV_GA4_EVENTS,
  FEEDBACK_FORM_URL,
} from 'components/ChatBV/constants';
import { ChatContext } from 'components/ChatBV/context';
import { useLimitedPlanInfo } from 'utils/useLimitedPlanInfo';

import { ConversationList } from '../../Sidebar/ChatHistory/ConversationList';
import { ProfileSection } from '../../Sidebar/ProfileSection';

interface ChatPageSidebarProps {
  onRecentReportsClick?: () => void;
  onCloseRecentReports?: () => void;
  onUpgradePlanClick?: () => void;
  onAccountClick?: () => void;
  onLogout?: () => void;
}

export const ChatPageSidebar = ({
  onRecentReportsClick,
  onCloseRecentReports,
  onUpgradePlanClick,
  onAccountClick,
  onLogout,
}: ChatPageSidebarProps) => {
  const theme = useTheme();
  const [openHelpModal, setOpenHelpModal] = useState(false);
  const [openProfileModal, setOpenProfileModal] = useState(false);
  const {
    handleNewChat,
    sidebarCollapsed: collapsed,
    setSidebarCollapsed,
  } = useContext(ChatContext);
  const [isNewChatHovered, setIsNewChatHovered] = useState(false);
  const [isReportLibraryHovered, setIsReportLibraryHovered] = useState(false);
  const [isChatHistoryHovered, setIsChatHistoryHovered] = useState(false);
  const [isAccountLinkHovered, setIsAccountLinkHovered] = useState(false);
  const [isProfileHovered, setIsProfileHovered] = useState(false);
  const { trackEventGA4 } = useContext(AppConfig);
  const { isDesktop } = useScreenSize();
  const { reportsRun, reportLimit, reportsRunPercentage, isLimitedUser } =
    useLimitedPlanInfo();
  const {
    session: { isAuthenticated },
  } = useSession();
  const { data: account } = useAccount(isAuthenticated);
  const profileRef = useRef<HTMLDivElement>(null);

  const handleNewChatClick = () => {
    handleNewChat();
    onCloseRecentReports?.();
    trackEventGA4({
      event_name: CHATBV_GA4_EVENTS.name,
      type: CHATBV_GA4_EVENTS.navSidebar.type,
      subtype: CHATBV_GA4_EVENTS.navSidebar.subTypes.new_chat,
    });
  };

  const handleToggleCollapsed = (value: boolean) => {
    setSidebarCollapsed(value && isDesktop);
    trackEventGA4({
      event_name: CHATBV_GA4_EVENTS.name,
      type: CHATBV_GA4_EVENTS.navSidebar.type,
      subtype: collapsed
        ? CHATBV_GA4_EVENTS.navSidebar.subTypes.expand
        : CHATBV_GA4_EVENTS.navSidebar.subTypes.collapse,
    });
  };

  const handleHelpModalClick = () => {
    setOpenHelpModal(true);
    trackEventGA4({
      event_name: CHATBV_GA4_EVENTS.name,
      type: CHATBV_GA4_EVENTS.navSidebar.type,
      subtype: CHATBV_GA4_EVENTS.navSidebar.subTypes.help_modal,
    });
  };

  const handleFeedbackFormClick = () => {
    window.open(FEEDBACK_FORM_URL, '_blank');
    trackEventGA4({
      event_name: CHATBV_GA4_EVENTS.name,
      type: CHATBV_GA4_EVENTS.navSidebar.type,
      subtype: CHATBV_GA4_EVENTS.navSidebar.subTypes.feedback_form,
    });
  };

  const handleRecentReportsClick = () => {
    onRecentReportsClick?.();
    trackEventGA4({
      event_name: CHATBV_GA4_EVENTS.name,
      type: CHATBV_GA4_EVENTS.navSidebar.type,
      subtype: 'report_library',
    });
  };

  const handleUpgradePlanClick = () => {
    onUpgradePlanClick?.();
    trackEventGA4({
      event_name: CHATBV_GA4_EVENTS.name,
      type: CHATBV_GA4_EVENTS.navSidebar.type,
      subtype: CHATBV_GA4_EVENTS.navSidebar.subTypes.upgrade_plan,
    });
  };

  const handleProfileClick = () => {
    setOpenProfileModal(true);
    trackEventGA4({
      event_name: CHATBV_GA4_EVENTS.name,
      type: CHATBV_GA4_EVENTS.navSidebar.type,
      subtype: 'profile_modal',
    });
  };

  const getInitial = () => {
    const firstName = account?.account?.user_info?.first_name;
    const lastName = account?.account?.user_info?.last_name;
    const email = account?.account?.user_info?.email;

    if (firstName) {
      return firstName[0].toUpperCase();
    }
    if (lastName) {
      return lastName[0].toUpperCase();
    }
    if (email) {
      return email[0].toUpperCase();
    }
    return 'U';
  };
  return (
    <>
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
        }}
      >
        <Stack
          height="100%"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            p: 2,
          }}
        >
          <Stack spacing={4} paddingTop={6} alignItems="center">
            <DarkTooltip title={collapsed ? 'Expand' : ''}>
              <SidebarIcon>
                {collapsed ? (
                  <KeyboardTab onClick={() => handleToggleCollapsed(false)} />
                ) : (
                  <KeyboardBackspace
                    onClick={() => handleToggleCollapsed(true)}
                  />
                )}
              </SidebarIcon>
            </DarkTooltip>
            <DarkTooltip title={collapsed ? 'New Chat' : ''}>
              <NewChatButton
                variant="contained"
                onClick={handleNewChatClick}
                onMouseEnter={() => setIsNewChatHovered(true)}
                onMouseLeave={() => setIsNewChatHovered(false)}
                sx={{
                  backgroundColor: isNewChatHovered
                    ? theme.palette.button.dark
                    : theme.palette.primary.main,
                }}
              >
                <AddIcon
                  sx={{
                    width: '20px',
                    height: '20px',
                    color: theme.palette.text.contrastText,
                  }}
                />
              </NewChatButton>
            </DarkTooltip>
            <DarkTooltip title={collapsed ? 'Report Library' : ''}>
              <SidebarIcon
                onClick={handleRecentReportsClick}
                sx={{
                  cursor: 'pointer',
                  '&:hover': {
                    color: theme.palette.primary.main,
                  },
                }}
                onMouseEnter={() => setIsReportLibraryHovered(true)}
                onMouseLeave={() => setIsReportLibraryHovered(false)}
              >
                <InsertDriveFileIcon
                  sx={{
                    width: '20px',
                    height: '20px',
                    color: isReportLibraryHovered
                      ? theme.palette.primary.main
                      : theme.palette.text.secondary,
                  }}
                />
              </SidebarIcon>
            </DarkTooltip>
            <DarkTooltip title={collapsed ? 'Chat History' : ''}>
              <SidebarIcon
                onClick={() => handleToggleCollapsed(!collapsed)}
                sx={{
                  cursor: 'pointer',
                  '&:hover': {
                    color: theme.palette.primary.main,
                  },
                }}
                onMouseEnter={() => setIsChatHistoryHovered(true)}
                onMouseLeave={() => setIsChatHistoryHovered(false)}
              >
                <QuestionAnswer
                  sx={{
                    width: '20px',
                    height: '20px',
                    color: isChatHistoryHovered
                      ? theme.palette.primary.main
                      : theme.palette.text.secondary,
                  }}
                />
              </SidebarIcon>
            </DarkTooltip>
          </Stack>
          <Stack
            sx={{ cursor: 'pointer' }}
            spacing={2}
            alignItems="center"
            paddingBottom={7.5}
            onClick={handleProfileClick}
            onMouseEnter={() => setIsProfileHovered(true)}
            onMouseLeave={() => setIsProfileHovered(false)}
          >
            <DarkTooltip title={collapsed ? 'Profile' : ''}>
              <Avatar
                sx={{
                  backgroundColor: isProfileHovered
                    ? theme.palette.button.dark
                    : theme.palette.primary.main,
                  fontSize: '12px',
                  width: '24px',
                  height: '24px',
                }}
              >
                {getInitial()}
              </Avatar>
            </DarkTooltip>
          </Stack>
        </Stack>
        <Stack
          height="100%"
          sx={{
            py: 2,
            transition: 'width 0.3s ease-in-out',
            transitionDelay: collapsed ? '0.1s' : '0',
          }}
          width={collapsed ? '0px' : '150px'}
        >
          <Stack
            spacing={3}
            paddingTop={5}
            sx={{
              opacity: collapsed ? 0 : 1,
              transition: 'opacity 0.2s ease-in-out',
              transitionDelay: collapsed ? '0' : '0.2s',
            }}
          >
            <BrandLogoChat width="108px" height="40px" />
            <Text
              sx={{
                fontWeight: 'bold',
                textWrap: 'nowrap',
                color: isNewChatHovered
                  ? theme.palette.primary.main
                  : theme.palette.text.primary,
                cursor: 'pointer',
                '&:hover': {
                  color: theme.palette.primary.main,
                },
                marginTop: '25px !important',
              }}
              onClick={handleNewChatClick}
              onMouseEnter={() => setIsNewChatHovered(true)}
              onMouseLeave={() => setIsNewChatHovered(false)}
            >
              New Chat
            </Text>
            <Text
              sx={{
                fontWeight: 'bold',
                textWrap: 'nowrap',
                color: isReportLibraryHovered
                  ? theme.palette.primary.main
                  : theme.palette.text.primary,
                cursor: 'pointer',
                '&:hover': {
                  color: theme.palette.primary.main,
                },
                marginTop: '39px !important',
              }}
              onClick={handleRecentReportsClick}
              onMouseEnter={() => setIsReportLibraryHovered(true)}
              onMouseLeave={() => setIsReportLibraryHovered(false)}
            >
              Report Library
            </Text>
            <Text
              sx={{
                fontWeight: 'bold',
                textWrap: 'nowrap',
                color: isChatHistoryHovered
                  ? theme.palette.primary.main
                  : theme.palette.text.primary,
                cursor: 'pointer',
                '&:hover': {
                  color: theme.palette.primary.main,
                },
                marginTop: '35px !important',
              }}
              onClick={() => handleToggleCollapsed(!collapsed)}
              onMouseEnter={() => setIsChatHistoryHovered(true)}
              onMouseLeave={() => setIsChatHistoryHovered(false)}
            >
              Chat History
            </Text>
          </Stack>
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              marginTop: 2,
              marginBottom: 3,

              // Firefox
              scrollbarWidth: 'thin',
              scrollbarColor: `${theme.palette.grey[500]} transparent`,

              // WebKit scrollbar
              '&::-webkit-scrollbar': {
                width: '6px',
                backgroundColor: 'transparent',
              },

              '&::-webkit-scrollbar-track': {
                backgroundColor: 'transparent',
              },

              '&::-webkit-scrollbar-thumb': {
                backgroundColor: theme.palette.grey[500],
                borderRadius: '8px',
              },
            }}
          >
            {!collapsed && <ConversationList collapsed={collapsed} />}
          </Box>
          <Stack
            spacing={3}
            paddingBottom={3.75}
            sx={{
              flexShrink: 0,
              marginTop: 'auto',
              opacity: collapsed ? 0 : 1,
              transition: 'opacity 0.2s ease-in-out',
              transitionDelay: collapsed ? '0s' : '0.2s',
            }}
          >
            {isLimitedUser && (
              <ReportUsageCard
                reportsRun={reportsRun}
                reportsLimit={reportLimit}
                progressValue={reportsRunPercentage}
                sx={{
                  marginLeft: `-48px !important`,
                  marginRight: '8px !important',
                }}
                onUpgradeClick={handleUpgradePlanClick}
              />
            )}
            {/* Account link */}
            <Box
              sx={{
                marginLeft: '-48px !important',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                padding: 1,
                cursor: 'pointer',
              }}
              onClick={onAccountClick}
              onMouseEnter={() => setIsAccountLinkHovered(true)}
              onMouseLeave={() => setIsAccountLinkHovered(false)}
            >
              <PersonIcon
                sx={{
                  width: '20px',
                  height: '20px',
                  marginTop: '-2px !important',
                  color: isAccountLinkHovered
                    ? theme.palette.primary.main
                    : theme.palette.text.secondary,
                }}
              />
              <Text
                sx={{
                  fontWeight: 'bold',
                  textWrap: 'nowrap',
                  color: isAccountLinkHovered
                    ? theme.palette.primary.main
                    : theme.palette.text.primary,
                  '&:hover': {
                    color: theme.palette.primary.main,
                  },
                }}
              >
                Account
              </Text>
            </Box>
            {/* Profile Section */}
            <Box
              ref={profileRef}
              onClick={handleProfileClick}
              onMouseEnter={() => setIsProfileHovered(true)}
              onMouseLeave={() => setIsProfileHovered(false)}
            >
              <ProfileSection account={account} isHovered={isProfileHovered} />
            </Box>
          </Stack>
        </Stack>
      </Box>
      <HelpModal open={openHelpModal} onClose={() => setOpenHelpModal(false)} />
      <ProfileModal
        open={openProfileModal}
        onClose={() => setOpenProfileModal(false)}
        onUpgradePlanClick={handleUpgradePlanClick}
        onHelpClick={handleHelpModalClick}
        onFeedbackClick={handleFeedbackFormClick}
        onLogout={onLogout}
      />
    </>
  );
};
